"use client";

import { useState, useRef, useEffect } from "react";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";

interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
}

export default function CustomDropdown({
  value,
  options,
  onChange,
  placeholder = "Select an option",
}: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedLabel =
    options.find((opt) => opt.value === value)?.label || placeholder;

  const handleSelectOption = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className="font-raleway flex min-h-[44px] w-full cursor-pointer appearance-none items-center rounded-full border border-white bg-transparent px-[20px] py-[10px] pr-12 text-left text-xs text-white outline-none focus:border-white"
      >
        {selectedLabel}
      </button>
      {isOpen ? (
        <IoChevronUp className="pointer-events-none absolute top-1/2 right-6 z-10 h-5 w-5 -translate-y-1/2 text-white" />
      ) : (
        <IoChevronDown className="pointer-events-none absolute top-1/2 right-6 z-10 h-5 w-5 -translate-y-1/2 text-white" />
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          role="listbox"
          className="bg-brand-dark-red no-scrollbar absolute top-full right-0 left-0 z-50 mt-2 max-h-52 overflow-y-auto rounded-2xl border border-white bg-linear-to-b shadow-2xl backdrop-blur-md"
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={value === option.value}
              onClick={() => handleSelectOption(option.value)}
              className={`font-raleway w-full cursor-pointer px-[20px] py-2.5 text-left text-xs font-bold text-white transition-colors hover:bg-white/15 ${
                value === option.value ? "bg-white/20" : ""
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}